/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2022, Zumper
 * @description Property Types Filter constants
 */

import { BuildingLabel, PropertyType } from '@zcode/enums'

export const TYPE_FILTER_OPTIONS_LIMIT = 8

export const TYPE_FILTER_LABEL_MAP = {
  houseBoat: 'House boat',
  guestHouse: 'Guest house',
} as const

export const PROPERTY_TYPE_GROUP_NAMES = {
  APARTMENT: 'apartment',
  HOUSE: 'house',
  CONDO: 'condo',
  ROOM: 'room',
  TOWNHOUSE: 'townhouse',
  OTHER: 'other',
}

export const APARTMENT_TYPE_GROUP = [
  PropertyType.APARTMENT,
  PropertyType.FLOOR_PLAN,
  PropertyType.LOFT,
  PropertyType.APARTMENT_CONDO_TOWNHOUSE,
  PropertyType.MULTI_FAMILY,
]

export const HOUSE_TYPE_GROUP = [PropertyType.SINGLE_FAMILY_HOME, PropertyType.HOUSE_RENTALS]

export const CONDO_TYPE_GROUP = [PropertyType.CONDO, PropertyType.COOP, PropertyType.TIC]

export const ROOM_TYPE_GROUP = [PropertyType.ROOM, PropertyType.SHARED_ROOM]

export const TOWNHOUSE_TYPE_GROUP = [PropertyType.TOWNHOUSE, PropertyType.APARTMENT_CONDO_TOWNHOUSE]

export const CONDOS_AND_APARTMENTS_TYPE_GROUP = [...APARTMENT_TYPE_GROUP, ...CONDO_TYPE_GROUP]

export const SHORT_TERM_OTHER_TYPE_GROUP = [
  PropertyType.UNKNOWN,
  PropertyType.MOBILE_MANUFACTURED,
  PropertyType.FARM_RANCH,
  PropertyType.INCOME_INVESTMENT,
  PropertyType.LOT_LAND,
]

export const LONG_TERM_OTHER_TYPE_GROUP = [
  PropertyType.UNKNOWN,
  PropertyType.MOBILE_MANUFACTURED,
  PropertyType.FARM_RANCH,
  PropertyType.INCOME_INVESTMENT,
  PropertyType.LOT_LAND,
  PropertyType.CABINS,
  PropertyType.COTTAGE,
  PropertyType.MANSIONS,
  PropertyType.VILLA,
  PropertyType.BOAT,
  PropertyType.BUNGALOW,
  PropertyType.HOUSEBOAT,
  PropertyType.GUEST_HOUSE,
]

export const PROPERTY_TYPE_NAME_TO_GROUP = {
  [PROPERTY_TYPE_GROUP_NAMES.APARTMENT]: APARTMENT_TYPE_GROUP,
  [PROPERTY_TYPE_GROUP_NAMES.HOUSE]: HOUSE_TYPE_GROUP,
  [PROPERTY_TYPE_GROUP_NAMES.CONDO]: CONDO_TYPE_GROUP,
  [PROPERTY_TYPE_GROUP_NAMES.ROOM]: ROOM_TYPE_GROUP,
  [PROPERTY_TYPE_GROUP_NAMES.TOWNHOUSE]: TOWNHOUSE_TYPE_GROUP,
} as const

export const BUILDING_LABEL_TO_GROUP_NAME = {
  [BuildingLabel.APARTMENTS]: PROPERTY_TYPE_GROUP_NAMES.APARTMENT,
  [BuildingLabel.BEDROOMS]: PROPERTY_TYPE_GROUP_NAMES.ROOM,
  [BuildingLabel.COMMUNITY]: PROPERTY_TYPE_GROUP_NAMES.APARTMENT,
  [BuildingLabel.CONDOMINIUMS]: PROPERTY_TYPE_GROUP_NAMES.CONDO,
  [BuildingLabel.CONDOS]: PROPERTY_TYPE_GROUP_NAMES.CONDO,
  [BuildingLabel.DORMS]: PROPERTY_TYPE_GROUP_NAMES.ROOM,
  [BuildingLabel.HOMES]: PROPERTY_TYPE_GROUP_NAMES.HOUSE,
  [BuildingLabel.LOFTS]: PROPERTY_TYPE_GROUP_NAMES.APARTMENT,
  [BuildingLabel.MILITARY_HOMES]: PROPERTY_TYPE_GROUP_NAMES.HOUSE,
  [BuildingLabel.ROOMS]: PROPERTY_TYPE_GROUP_NAMES.ROOM,
  [BuildingLabel.TOWNHOUSES]: PROPERTY_TYPE_GROUP_NAMES.TOWNHOUSE,
}

export const SHORT_TERM_PROPERTY_TYPE_NAME_TO_GROUP = {
  ...PROPERTY_TYPE_NAME_TO_GROUP,
  [PROPERTY_TYPE_GROUP_NAMES.OTHER]: SHORT_TERM_OTHER_TYPE_GROUP,
} as const

export const LONG_TERM_PROPERTY_TYPE_NAME_TO_GROUP = {
  ...PROPERTY_TYPE_NAME_TO_GROUP,
  [PROPERTY_TYPE_GROUP_NAMES.OTHER]: LONG_TERM_OTHER_TYPE_GROUP,
}

export const SHORT_TERM_PROPERTY_TYPE_OPTIONS_KEY_MAP = {
  cabin: PropertyType.CABINS,
  cottage: PropertyType.COTTAGE,
  mansion: PropertyType.MANSIONS,
  villa: PropertyType.VILLA,
  boat: PropertyType.BOAT,
  bungalow: PropertyType.BUNGALOW,
  houseBoat: PropertyType.HOUSEBOAT,
  guestHouse: PropertyType.GUEST_HOUSE,
  hotel: PropertyType.HOTEL,
} as const

export const SHORT_TERM_PROPERTY_TYPE_KEY_TO_LABEL = {
  [PropertyType.CABINS]: 'Cabin',
  [PropertyType.COTTAGE]: 'Cottage',
  [PropertyType.MANSIONS]: 'Mansion',
  [PropertyType.VILLA]: 'Villa',
  [PropertyType.BOAT]: 'Boat',
  [PropertyType.BUNGALOW]: 'Bungalow',
  [PropertyType.HOUSEBOAT]: 'House boat',
  [PropertyType.GUEST_HOUSE]: 'Guest house',
  [PropertyType.HOTEL]: 'Hotel',
}

export const SHORT_TERM_PROPERTY_TYPE_OPTIONS = [
  'cabin',
  'cottage',
  'mansion',
  'house',
  'hotel',
  'villa',
  'boat',
  'condo',
  'apartment',
  'bungalow',
  'houseBoat',
  'guestHouse',
  'other',
] as const

export const LONG_TERM_PROPERTY_TYPE_OPTIONS = [
  'apartment',
  'condo',
  'house',
  'room',
  'townhouse',
  'other',
] as const

export const PROPERTY_TYPES_SNAKE_CASE = {
  [PropertyType.CABINS]: 'cabin',
  [PropertyType.COTTAGE]: 'cottage',
  [PropertyType.MANSIONS]: 'mansion',
  [PropertyType.VILLA]: 'villa',
  [PropertyType.BOAT]: 'boat',
  [PropertyType.BUNGALOW]: 'bungalow',
  [PropertyType.HOUSEBOAT]: 'house-boat',
  [PropertyType.GUEST_HOUSE]: 'guest-house',
  [PropertyType.HOTEL]: 'hotel',
  [PropertyType.APARTMENT]: 'apartment',
  [PropertyType.APARTMENT_CONDO_TOWNHOUSE]: 'apartment-condo-townhouse',
  [PropertyType.CONDO]: 'condo',
  [PropertyType.COOP]: 'coop',
  [PropertyType.FLOOR_PLAN]: 'floor-plan',
  [PropertyType.LOFT]: 'loft',
  [PropertyType.MULTI_FAMILY]: 'multi-family',
  [PropertyType.ROOM]: 'room',
  [PropertyType.SHARED_ROOM]: 'shared-room',
  [PropertyType.SINGLE_FAMILY_HOME]: 'single-family-home',
  [PropertyType.TIC]: 'tic',
  [PropertyType.TOWNHOUSE]: 'townhouse',
  [PropertyType.UNKNOWN]: 'unknown',
  [PropertyType.MOBILE_MANUFACTURED]: 'mobile-manufactured',
  [PropertyType.FARM_RANCH]: 'farm-ranch',
  [PropertyType.INCOME_INVESTMENT]: 'income-investment',
  [PropertyType.LOT_LAND]: 'lot-land',
  [PropertyType.HOUSE_RENTALS]: 'house-rentals',
  [PropertyType.LUXURY]: 'luxury',
}

export const ALL_PROPERTY_TYPES = [
  ...SHORT_TERM_PROPERTY_TYPE_OPTIONS,
  ...LONG_TERM_PROPERTY_TYPE_OPTIONS,
  'apartment-condo-townhouse',
  'coop',
  'floor-plan',
  'loft',
  'multi-family',
  'shared-room',
  'single-family-home',
  'tic',
  'unknown',
  'mobile-manufactured',
  'income-investment',
  'lot-land',
  'house-rentals',
]
